import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { DraftUntitledError } from "@/stores/pm/IssuesStore";
import {
  draftHasContent,
  draftTitleIsEmpty,
  storedDraftLeaveAction,
  type IssueDraft,
  type IssueDraftDefaults,
} from "@/domains/pm/issueDraft";
import { useLeaveGuard, type LeaveTarget } from "@/lib/navigation/useLeaveGuard";
import { billingGuardMessage } from "@/domains/billing/messages";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import type { Translations } from "@/lib/i18n/types";
import type { PMIssue } from "@/domains/pm/types";

interface Options {
  buildingId: number;
  orgId: string | null | undefined;
  userProfileId: number | null | undefined;
  /**
   * The project the page is scoped to. A draft belongs to the project it was
   * started in: only that project's drafts are drawn, opened or saved here,
   * and a change of scope takes the open one out of the panel.
   */
  selectedProjectId: number | null;
  /**
   * The protocol wizard is open. It owns the issue selection while it is (its
   * embedded modal writes the shared slot), so its picks are not leaves and a
   * failed save does not take the panel back from it.
   */
  isProtocolOpen: boolean;
}

/** A leave held back until the person answers the "needs a description" warning. */
interface HeldDraftLeave {
  proceed: () => void;
  stay?: () => void;
}

/** What a failed save reads as: the plan refusal if it is one, else the cleaned-up error. */
function draftSaveError(error: unknown, t: Translations): string {
  return billingGuardMessage(error, t) ?? friendlyIssueError(error, t);
}

function newestFirst(a: IssueDraft, b: IssueDraft) {
  // Ids count down, so the most negative is the newest.
  return a.id - b.id;
}

/**
 * The /issues page's New issue draft: starting one, what leaving it does, and
 * the ways out of the page that count as leaving.
 *
 * The draft itself lives in `IssuesStore` (so a save outlives the page); this
 * hook is the page's side of it. Leaving — closing the panel, opening another
 * issue, starting another draft, the protocol wizard, a scope change, another
 * route — goes through `leaveDraft`, which saves a titled draft, drops an
 * untouched one without a word, and holds the leave behind `warning` when
 * something was entered but there is no description to save it under.
 * Switching List / Kanban / Gantt is not a leave.
 */
export function useIssuesPageDraft({
  buildingId,
  orgId,
  userProfileId,
  selectedProjectId,
  isProtocolOpen,
}: Options) {
  const { t } = useLanguage();
  const issuesStore = useIssuesStore();

  // The scope in force, for callbacks that settle later (a save, a toast's
  // action). Moved by the scope-change effect below, not by render.
  const scopeRef = useRef(selectedProjectId);
  /** Is this draft one this page, scoped as it is now, may show and save? */
  const isOwnDraft = (draft: IssueDraft | null, scope: number | null): boolean =>
    draft != null &&
    draft.buildingId === buildingId &&
    scope != null &&
    draft.values.projectId === scope;
  const isOwnDraftRef = useRef(isOwnDraft);
  isOwnDraftRef.current = isOwnDraft;

  const activeDraft = isOwnDraft(issuesStore.activeDraft, selectedProjectId)
    ? issuesStore.activeDraft
    : null;
  const buildingDrafts = issuesStore.drafts.filter((d) => d.buildingId === buildingId);
  /** What the views draw: this project's drafts, newest first — the open one,
   *  and any still being saved or waiting after a failed save. */
  const draftRows: readonly PMIssue[] = issuesStore.draftIssuesFor(buildingId, selectedProjectId);
  const savingDraftIds = new Set(
    buildingDrafts.filter((d) => d.phase === "committing").map((d) => d.id)
  );

  const [warning, setWarning] = useState<HeldDraftLeave | null>(null);
  // Read by the warning's buttons and its close, which can both fire for one
  // click (AlertDialogAction runs its onClick, then closes the dialog).
  const warningRef = useRef<HeldDraftLeave | null>(null);
  const [titleFocusToken, setTitleFocusToken] = useState(0);
  const focusTitle = () => setTitleFocusToken((n) => n + 1);

  // The commit can settle after the page has gone; the toast still belongs.
  const tRef = useRef(t);
  tRef.current = t;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const protocolOpenRef = useRef(isProtocolOpen);
  protocolOpenRef.current = isProtocolOpen;

  /**
   * A save failed. The draft comes back to the panel only when this page is
   * the one showing and nothing else holds it — no issue open, no other draft,
   * no wizard. Otherwise it waits as a row (and the toast offers to open it);
   * this page reopens a waiting draft when it next mounts. The store never
   * takes the panel itself: the selection is shared with My Issues and the
   * viewers, where taking it would close what the person has open.
   */
  const reclaimFailedDraft = useCallback(
    (draftId: number): boolean => {
      if (!mountedRef.current || protocolOpenRef.current) return false;
      if (issuesStore.activeDraft != null || issuesStore.selectedIssue != null) return false;
      // Only into its own project's panel: elsewhere it waits as a row there.
      if (!isOwnDraftRef.current(issuesStore.findDraft(draftId), scopeRef.current)) return false;
      return issuesStore.reopenDraft(draftId);
    },
    [issuesStore]
  );

  const commit = useCallback(
    (draftId: number) => {
      void issuesStore.commitDraft(draftId).then((result) => {
        // `in`, not `result.ok`: without strictNullChecks a boolean
        // discriminant does not narrow the union.
        if (!("error" in result)) return;
        const copy = tRef.current;
        if (result.error instanceof DraftUntitledError) {
          // Refused before anything was written: no description, or no project.
          const values = issuesStore.findDraft(draftId)?.values;
          toast.error(
            values && !draftTitleIsEmpty(values)
              ? copy.pm.issues.draft.noProject
              : copy.pm.issues.draft.createNeedsDescription
          );
          return;
        }
        const draft = issuesStore.findDraft(draftId);
        if (reclaimFailedDraft(draftId)) {
          toast.error(copy.pm.issues.draft.saveFailed, {
            description: draftSaveError(result.error, copy),
          });
          return;
        }
        toast.error(copy.pm.issues.draft.saveFailedWaiting, {
          description: draftSaveError(result.error, copy),
          // Only while this page can show it; elsewhere it reopens on return.
          action:
            mountedRef.current && isOwnDraftRef.current(draft, scopeRef.current)
              ? {
                  label: copy.pm.issues.draft.reopen,
                  onClick: () => {
                    if (mountedRef.current) selectDraftRef.current(draftId);
                  },
                }
              : undefined,
        });
      });
    },
    [issuesStore, reclaimFailedDraft]
  );

  /** Drop a draft; a draft whose issue exists could not be deleted → say so. */
  const discard = useCallback(
    (draftId: number) => {
      void issuesStore.discardDraft(draftId).then((ok) => {
        if (ok || issuesStore.findDraft(draftId)?.phase !== "failed") return;
        toast.error(tRef.current.pm.issues.draft.discardFailed);
      });
    },
    [issuesStore]
  );

  /**
   * Leave the open draft, then `proceed`. With no draft open it just proceeds.
   * `stay` is called instead when the person keeps the draft (or a save is
   * refused) — a held navigation has to be told.
   */
  const leaveDraft = (proceed: () => void, stay?: () => void) => {
    const draft = issuesStore.activeDraft;
    if (!draft || !isOwnDraft(draft, scopeRef.current)) {
      proceed();
      return;
    }
    // An attachment is still uploading: saving now would file the issue
    // without it, and discarding could not bin it. The leave waits.
    if (draft.uploading) {
      toast.error(t.pm.issues.draft.uploadInProgress);
      stay?.();
      return;
    }
    switch (storedDraftLeaveAction(draft)) {
      case "commit":
        commit(draft.id);
        // Still open: the save was refused before it started (no project). Its
        // toast says why; whatever was leaving waits.
        if (issuesStore.activeDraftId === draft.id) {
          stay?.();
          return;
        }
        proceed();
        return;
      case "discard":
        discard(draft.id);
        proceed();
        return;
      case "confirm":
        warningRef.current = { proceed, stay };
        setWarning(warningRef.current);
        return;
    }
  };
  const leaveDraftRef = useRef(leaveDraft);
  leaveDraftRef.current = leaveDraft;

  /** Open a new draft in the panel — after leaving the one already open. */
  const startDraft = (defaults: IssueDraftDefaults) =>
    leaveDraft(() => {
      issuesStore.startDraft({ buildingId, orgId, userProfileId, defaults });
    });

  /** Create: save and close. An empty description is asked for, not saved. */
  const createDraft = () => {
    const draft = activeDraft;
    if (!draft) return;
    // Never file into a project other than the one the page is on. The panel
    // only shows this project's draft, so this is the backstop, not a path.
    if (!isOwnDraft(draft, scopeRef.current)) {
      toast.warning(t.pm.issues.draft.noProject, { id: "new-issue:no-project" });
      return;
    }
    if (draft.uploading) {
      toast.error(t.pm.issues.draft.uploadInProgress);
      return;
    }
    if (draftTitleIsEmpty(draft.values)) {
      toast.error(t.pm.issues.draft.createNeedsDescription);
      focusTitle();
      return;
    }
    commit(draft.id);
  };

  /**
   * A draft's row was clicked: the open one gets its description focused; a
   * waiting one (its save failed) comes back to the panel, after leaving
   * whichever draft is open now.
   */
  const selectDraft = (draftId: number) => {
    if (activeDraft?.id === draftId) {
      focusTitle();
      return;
    }
    if (!isOwnDraft(issuesStore.findDraft(draftId), scopeRef.current)) return;
    leaveDraft(() => {
      issuesStore.reopenDraft(draftId);
    });
  };
  const selectDraftRef = useRef(selectDraft);
  selectDraftRef.current = selectDraft;

  const resolveWarning = (choice: "stay" | "discard") => {
    const held = warningRef.current;
    if (!held) return;
    warningRef.current = null;
    setWarning(null);
    if (choice === "stay") {
      held.stay?.();
      focusTitle();
      return;
    }
    const draftId = issuesStore.activeDraftId;
    if (draftId != null) discard(draftId);
    held.proceed();
  };

  // ---- Leaving the page by navigation -----------------------------------
  // Another route, or another `?project=` than the scope in force. Compared
  // with the RESOLVED scope rather than the old URL: switching to Building
  // view drops the scope first and strips the param after, and that strip is
  // not a second leave.
  const leavesIssues = useCallback((target: LeaveTarget, from: LeaveTarget) => {
    if (target.pathname !== from.pathname) return true;
    const raw = new URLSearchParams(target.search).get("project");
    const next = raw != null && /^\d+$/.test(raw) ? Number(raw) : null;
    return next !== scopeRef.current;
  }, []);
  const { held } = useLeaveGuard(activeDraft != null, leavesIssues);
  useEffect(() => {
    if (held) leaveDraftRef.current(held.proceed, held.stay);
  }, [held]);

  // A scope change no navigation announced (the scope store resolving a
  // remembered project, a project the options rejected) is a leave after the
  // fact: the page is already on the new project, so there is nobody left to
  // ask. A titled draft is saved into ITS project, an untouched one dropped,
  // and one with input but no description — or an upload running — waits out
  // of the panel until its project is in scope again. It is never left open
  // under a project it does not belong to. The first resolution of the scope
  // (null → a project, the one render before the layout settles) is not a
  // leave: nothing could have been opened under no project.
  useEffect(() => {
    const previous = scopeRef.current;
    if (previous === selectedProjectId) return;
    scopeRef.current = selectedProjectId;
    if (previous == null) return;
    const draft = issuesStore.activeDraft;
    if (!draft || !isOwnDraftRef.current(draft, previous)) return;
    // A warning held for this draft no longer has a page to stay on. Whatever
    // it held back (a link, opening an issue) was meant for the old project,
    // so it is told to stay rather than carried out here.
    const heldLeave = warningRef.current;
    warningRef.current = null;
    setWarning(null);
    heldLeave?.stay?.();
    const action = draft.uploading ? "park" : storedDraftLeaveAction(draft);
    if (action === "commit") commit(draft.id);
    else if (action === "discard") discard(draft.id);
    else {
      issuesStore.setDraftUploading(draft.id, false);
      issuesStore.parkDraft(draft.id);
    }
  }, [selectedProjectId, issuesStore, commit, discard]);

  // Whatever else leaves a draft open in the panel for another project (one
  // the page left open when it went, a scope settling late) is parked: its
  // project's page reopens it.
  const storeActive = issuesStore.activeDraft;
  const strayDraftId =
    storeActive != null &&
    storeActive.buildingId === buildingId &&
    selectedProjectId != null &&
    !isOwnDraft(storeActive, selectedProjectId)
      ? storeActive.id
      : null;
  useEffect(() => {
    if (strayDraftId == null) return;
    issuesStore.setDraftUploading(strayDraftId, false);
    issuesStore.parkDraft(strayDraftId);
  }, [strayDraftId, issuesStore]);

  // An issue opened while the draft is open by anything other than a row click
  // (a menu's "Add checklist", a ?selected= link) is a leave too. The draft
  // panel is drawn over the selection, so keeping the draft drops the pick.
  // Not the protocol wizard's picks: it owns the selection while it is open.
  const selectedIssueId = issuesStore.selectedIssue?.id ?? null;
  const draftOpen = activeDraft != null;
  useEffect(() => {
    if (selectedIssueId == null || !draftOpen || isProtocolOpen) return;
    leaveDraftRef.current(
      () => undefined,
      () => issuesStore.clearSelection()
    );
  }, [selectedIssueId, draftOpen, isProtocolOpen, issuesStore]);

  // Closing or reloading the tab: an asynchronous save cannot be relied on to
  // finish, so the browser asks while anything would be lost.
  const unsaved =
    (activeDraft != null &&
      (activeDraft.uploading || draftHasContent(activeDraft.values, activeDraft.initial))) ||
    buildingDrafts.some((d) => d.phase === "committing");
  useEffect(() => {
    if (!unsaved) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome < 119 and Safari need returnValue set to show the prompt.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [unsaved]);

  // Back on the page, or on the project: a draft of this project whose save
  // failed after the page went (or one left waiting) comes back to its panel
  // rather than lingering unseen. Not before the scope is known, and never a
  // draft of another project.
  useEffect(() => {
    if (selectedProjectId == null || issuesStore.activeDraft) return;
    const waiting = issuesStore.drafts
      .filter((d) => d.phase !== "committing" && isOwnDraftRef.current(d, selectedProjectId))
      .sort(newestFirst)[0];
    if (waiting) issuesStore.reopenDraft(waiting.id);
  }, [issuesStore, buildingId, selectedProjectId]);

  // Unmounting is the last leave, and My Issues shares the store. The route
  // guard has asked already when it could; a draft it could not ask about and
  // that has input but no description stays open for the next visit rather
  // than being thrown away unasked.
  useEffect(() => {
    return () => {
      const draft = issuesStore.activeDraft;
      if (!draft || !isOwnDraftRef.current(draft, scopeRef.current)) return;
      // The panel is going with the page, so it can no longer report its
      // upload; one still running is placed by the store when it lands.
      issuesStore.setDraftUploading(draft.id, false);
      const action = storedDraftLeaveAction(draft);
      if (action === "commit") commit(draft.id);
      else if (action === "discard") discard(draft.id);
    };
  }, [issuesStore, buildingId, commit, discard]);

  const draftError =
    activeDraft?.error != null && !(activeDraft.error instanceof DraftUntitledError)
      ? draftSaveError(activeDraft.error, t)
      : null;

  return {
    activeDraft,
    draftRows,
    savingDraftIds,
    draftError,
    leaveDraft,
    startDraft,
    createDraft,
    selectDraft,
    titleFocusToken,
    warning: {
      open: warning != null,
      addDescription: () => resolveWarning("stay"),
      discard: () => resolveWarning("discard"),
    },
  };
}
